/**
 * CreateNewModal — FU-E sidebar entry point.
 *
 * Opened from the rail's "+ Create new" button. Shows a single picker
 * grid of artifact kinds; clicking a tile opens the matching detail
 * modal.
 *
 * Six tiles at most, each gated on the slug the BE checks for it: the
 * four card kinds need `create-deliverables`, Project needs
 * `create-projects` (owner + admin only), and Time entry is ungated for
 * every non-guest — which is why a `member` / `commenter` / `reader`
 * ends up with exactly one thing they can create. When that happens the
 * picker step is skipped entirely and the sole action's own dialog opens
 * straight away: a grid that asks a question with a single answer costs
 * a click and teaches nothing. (A guest reaches none of this — the
 * sidebar trigger is hidden for them, because Time entry 403s too.)
 *
 * Where each tile goes:
 *   - Task / Ticket / Event / Announcement → routes to the project's
 *     board with `?create=<kind>` so `<BoardPage>` opens its
 *     `<CardCreateModal>` pre-locked to that kind.
 *   - Time entry → opens `<TimeEntryModal mode="create">` directly. No
 *     project context required (the picker inside the modal walks the
 *     user through choosing a deliverable).
 *   - Project → opens `<ProjectCreateModal>` directly. The modal handles
 *     its own success-navigation into the new board.
 *
 * Project picker step:
 *   For card kinds (Task/Ticket/Event/Announcement) we need a project
 *   id. If the sidebar already knows one (route param), use it. Otherwise
 *   we surface a small "Which project?" picker step before navigating.
 *   The picker is a flat dropdown — the full ProjectSwitcher in
 *   `<AppShell>` is the heavy version; for one-shot routing this is
 *   intentionally lighter.
 */

import {
  CalendarDays,
  CheckSquare,
  Clock,
  FolderPlus,
  type LucideIcon,
  Megaphone,
  Ticket,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { DeliverableKind, Project } from '@/api/types-bridge'
import { Modal } from '@/components/Modal'
import { CardCreateModal } from '@/features/board/components/CardCreateModal'
import { useCardPermissions } from '@/features/me/use-card-permissions'
import { useTenantPermissions } from '@/features/me/use-tenant-permissions'
import { ProjectCreateModal } from '@/features/projects/components/ProjectCreateModal'
import { TimeEntryModal } from '@/features/time/components/TimeEntryModal'

interface CreateNewModalProps {
  open: boolean
  onClose: () => void
  /** Active project id from the route, or null if not on a project page. */
  currentProjectId: string | null
  /** Authoritative project list (from `useProjects()`). */
  projects: readonly Project[]
}

/**
 * One picker tile — large icon, bold name, secondary description.
 * Hover lifts the border to slate-300 and tints the icon background.
 */
interface KindTileProps {
  icon: LucideIcon
  iconClassName?: string
  iconBgClassName?: string
  name: string
  description: string
  onClick: () => void
}

function KindTile({
  icon: Icon,
  iconClassName = 'text-fg',
  iconBgClassName = 'bg-bg-muted',
  name,
  description,
  onClick,
}: KindTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col items-start gap-2 rounded-lg border border-border bg-bg-elevated p-3 text-start transition-all hover:-translate-y-px hover:border-border-strong hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-accent"
    >
      <span
        className={`inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors ${iconBgClassName}`}
      >
        <Icon size={18} strokeWidth={1.75} className={iconClassName} aria-hidden="true" />
      </span>
      <span className="text-sm font-semibold text-fg">{name}</span>
      <span className="text-xs leading-snug text-fg-muted">{description}</span>
    </button>
  )
}

/** What was clicked + whether we still need a project picker step. */
type PendingAction =
  | { kind: 'card'; deliverableKind: DeliverableKind }
  | { kind: 'time-entry' }
  | { kind: 'project' }
  | null

export function CreateNewModal({ open, onClose, currentProjectId, projects }: CreateNewModalProps) {
  const { t } = useTranslation('common')
  const { canCreateCards } = useCardPermissions()
  const { canCreateProjects } = useTenantPermissions()
  // Time entry has no slug of its own on the BE (only the guest ceiling
  // refuses it), so it is the one tile that always survives — which
  // guarantees the picker is never empty for anyone who can open it.
  const tileCount = (canCreateCards ? 4 : 0) + 1 + (canCreateProjects ? 1 : 0)
  // Which leaves Time entry as the only possible sole survivor: any
  // other grant brings a sibling tile with it.
  const soleAction: PendingAction = tileCount > 1 ? null : { kind: 'time-entry' }

  // What did the user just click? Drives the secondary-modal branches.
  const [pending, setPending] = useState<PendingAction>(null)

  // Read the sole action on the OPEN transition rather than on every
  // render: `useCardPermissions` / `useTenantPermissions` fall OPEN
  // while `/api/me` is in flight, so a late answer must not yank the
  // grid out from under someone already looking at it.
  const soleActionRef = useRef(soleAction)
  soleActionRef.current = soleAction

  // Opening either lands on the picker or, for a caller with exactly one
  // create verb, on that verb's dialog. Closing resets so the next open
  // starts clean.
  useEffect(() => {
    setPending(open ? soleActionRef.current : null)
  }, [open])

  /* ------------------------------ click handlers ------------------------------ */

  const handleCardKindClick = (k: DeliverableKind) => {
    // Open `<CardCreateModal>` inline (in this same component) instead
    // of navigating to the project board — matches the legacy "single
    // modal with title + project + create" flow. The modal carries its
    // own Project picker when no project context is pre-supplied (i.e.
    // when on the master surface), so the user picks the project there
    // alongside the title + kind-specific fields.
    setPending({ kind: 'card', deliverableKind: k })
  }

  const handleTimeEntryClick = () => {
    setPending({ kind: 'time-entry' })
  }

  const handleProjectClick = () => {
    setPending({ kind: 'project' })
  }

  // For the card / time-entry / project branches we render the secondary
  // modal INSTEAD of the picker grid, with this modal closed.
  const showPicker = pending === null
  const showCardCreate = pending?.kind === 'card'
  const showTimeEntry = pending?.kind === 'time-entry'
  const showProjectCreate = pending?.kind === 'project'

  // Default initial start/end for a brand-new time entry. Snap to the
  // top of the current hour, 30-min duration. No need to be precise —
  // the modal lets the user adjust.
  const [timeEntryDefaults] = useState(() => {
    const start = new Date()
    start.setMinutes(0, 0, 0)
    const end = new Date(start.getTime() + 30 * 60 * 1000)
    return { start, end }
  })

  return (
    <>
      <Modal open={open && showPicker} onClose={onClose} ariaLabel={t('createNew.ariaLabel')}>
        <Modal.Header>{t('createNew.title')}</Modal.Header>
        <Modal.Body>
          {showPicker && (
            <div
              // Six tiles read as two rows of three; the shorter sets the
              // permission gates leave read as one row, so the
              // three-across track is dropped rather than leaving a hole.
              className={
                tileCount > 4 ? 'grid grid-cols-2 gap-3 sm:grid-cols-3' : 'grid grid-cols-2 gap-3'
              }
              data-testid="create-new-picker"
            >
              {/*
               * Filled colored icon backgrounds (white icon on the kind's
               * brand color) — matches the legacy Create New picker
               * aesthetic. Each tile carries its kind's color identity
               * (sky / emerald / amber / rose / violet / slate) so the
               * Picker is visually scannable at a glance, and downstream
               * surfaces (board card stripes, kind icons in column
               * headers) reuse the same palette.
               */}
              {canCreateCards && (
                <>
                  <KindTile
                    icon={CheckSquare}
                    iconClassName="text-white"
                    iconBgClassName="bg-sky-500 group-hover:bg-sky-600"
                    name={t('createNew.tiles.task.name')}
                    description={t('createNew.tiles.task.description')}
                    onClick={() => handleCardKindClick('task')}
                  />
                  <KindTile
                    icon={Ticket}
                    iconClassName="text-white"
                    iconBgClassName="bg-emerald-500 group-hover:bg-emerald-600"
                    name={t('createNew.tiles.ticket.name')}
                    description={t('createNew.tiles.ticket.description')}
                    onClick={() => handleCardKindClick('ticket')}
                  />
                  <KindTile
                    icon={CalendarDays}
                    iconClassName="text-white"
                    iconBgClassName="bg-amber-500 group-hover:bg-amber-600"
                    name={t('createNew.tiles.event.name')}
                    description={t('createNew.tiles.event.description')}
                    onClick={() => handleCardKindClick('event')}
                  />
                  <KindTile
                    icon={Megaphone}
                    iconClassName="text-white"
                    iconBgClassName="bg-rose-500 group-hover:bg-rose-600"
                    name={t('createNew.tiles.announcement.name')}
                    description={t('createNew.tiles.announcement.description')}
                    onClick={() => handleCardKindClick('announcement')}
                  />
                </>
              )}
              <KindTile
                icon={Clock}
                iconClassName="text-white"
                iconBgClassName="bg-violet-500 group-hover:bg-violet-600"
                name={t('createNew.tiles.timeEntry.name')}
                description={t('createNew.tiles.timeEntry.description')}
                onClick={handleTimeEntryClick}
              />
              {canCreateProjects && (
                <KindTile
                  icon={FolderPlus}
                  // The five kind tiles fill with a saturated hue and take a
                  // white glyph; Project is deliberately the neutral one, so
                  // its fill is the ink token. That flips on the dark themes
                  // — `--color-fg` is near-white there — and a white glyph on
                  // it rendered as a blank white square. The glyph follows
                  // the fill to the canvas token, which is `fg`'s opposite
                  // pole in every theme by construction.
                  iconClassName="text-white dark:text-bg"
                  iconBgClassName="bg-fg group-hover:opacity-90"
                  name={t('createNew.tiles.project.name')}
                  description={t('createNew.tiles.project.description')}
                  onClick={handleProjectClick}
                />
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Card create — opens directly with the chosen kind. The modal
          carries its own Project picker (pre-selected to the user's
          current project board context, or empty when on the master
          surface; the user can change it before creating). */}
      {showCardCreate && pending?.kind === 'card' && (
        <CardCreateModal
          open
          onClose={() => {
            setPending(null)
            onClose()
          }}
          projectId={currentProjectId ?? ''}
          projects={projects}
          defaultStatusId=""
          defaultKind={pending.deliverableKind}
        />
      )}

      {/* Secondary modals — these are independent dialogs, so they
          render in their own portal. The Create-New picker stays closed
          while they're up; closing them returns to a clean state. */}
      {showTimeEntry && (
        <TimeEntryModal
          open={true}
          mode="create"
          initialStart={timeEntryDefaults.start}
          initialEnd={timeEntryDefaults.end}
          initialProjectId={currentProjectId ?? undefined}
          onClose={() => {
            setPending(null)
            onClose()
          }}
        />
      )}

      {showProjectCreate && (
        <ProjectCreateModal
          open={true}
          onClose={() => {
            setPending(null)
            onClose()
          }}
        />
      )}
    </>
  )
}
